import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2,
  Trophy,
  CheckCircle2,
  XCircle,
  Ban,
  MapPin,
  Clock,
} from "lucide-react";

/**
 * Public staff-facing redemption page reached via QR scan.
 * URL: /redeem/DUGS-XXXX-YYYY
 *
 * Flow:
 * 1. Load voucher details (validates + shows current state).
 * 2. If unused → staff enters optional venue + note, clicks Confirm.
 * 3. Backend atomically flips redeemed_at (single-use, race-safe).
 * 4. Success screen replaces the form.
 * 5. If already used on load → show the "already redeemed" state.
 */
export default function StaffRedemptionPage() {
  const { code } = useParams();
  const [status, setStatus] = useState("loading"); // loading | ready | used | error | success
  const [reward, setReward] = useState(null);
  const [location, setLocation] = useState("");
  const [staffNote, setStaffNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setStatus("loading");
    setError("");
    try {
      const { data } = await api.get(`/rewards/validate/${encodeURIComponent(code)}`);
      setReward(data);
      if (data.redeemed_at) {
        setStatus("used");
      } else if (data.expires_at) {
        // Simple client-side expiry preview — the backend is the source of truth
        // and will block the POST /redeem with 410 if it has actually expired.
        const today = new Date().toISOString().slice(0, 10);
        setStatus(data.expires_at < today ? "expired" : "ready");
      } else {
        setStatus("ready");
      }
    } catch (e) {
      setError(formatApiError(e));
      setStatus("error");
    }
  }, [code]);
  useEffect(() => { load(); }, [load]);

  const confirm = async () => {
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post(`/rewards/redeem/${encodeURIComponent(code)}`, {
        location: location.trim() || null,
        staff_note: staffNote.trim() || null,
      });
      setReward(data);
      setStatus("success");
    } catch (e) {
      // 409 → already redeemed → show the used state
      if (e?.response?.status === 409) {
        await load();
        return;
      }
      // 410 → expired → show the expired state
      if (e?.response?.status === 410) {
        setStatus("expired");
        setError(e?.response?.data?.detail?.message || "This voucher has expired.");
        return;
      }
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-zinc-950 text-white flex items-center justify-center px-4 py-10"
      data-testid="staff-redemption-page"
    >
      <div className="w-full max-w-xl">
        <div className="text-center mb-6">
          <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-amber-400 inline-flex items-center gap-1.5">
            <Trophy className="h-3 w-3" /> Dugsy&apos;s · Staff redemption
          </p>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold tracking-tight">
            Voucher check.
          </h1>
        </div>

        {status === "loading" && (
          <div className="bg-white text-zinc-950 rounded-sm p-10 text-center" data-testid="redeem-loading">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-zinc-400" />
            <p className="mt-3 text-sm text-zinc-500">Looking up voucher…</p>
          </div>
        )}

        {status === "error" && (
          <div className="bg-white text-zinc-950 rounded-sm p-8" data-testid="redeem-error">
            <XCircle className="h-10 w-10 text-rose-600 mx-auto" />
            <h2 className="mt-4 font-heading text-2xl font-bold text-center">Not a valid voucher.</h2>
            <p className="mt-2 text-sm text-zinc-600 text-center">{error || "This code doesn't match any voucher on file."}</p>
            <p className="mt-1 text-xs text-zinc-400 text-center font-mono">{code}</p>
          </div>
        )}

        {(status === "ready" || status === "used" || status === "success" || status === "expired") && reward && (
          <div className="bg-white text-zinc-950 rounded-sm overflow-hidden" data-testid={`redeem-panel-${status}`}>
            {/* Prize header */}
            <div
              className={`px-6 py-5 text-white ${
                status === "used"
                  ? "bg-zinc-700"
                  : status === "success"
                  ? "bg-emerald-600"
                  : status === "expired"
                  ? "bg-rose-700"
                  : "bg-amber-600"
              }`}
            >
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] bg-white/20">
                  <Trophy className="h-3 w-3" />
                  {reward.rank === 1 ? "1st place" : reward.rank === 2 ? "2nd place" : `${reward.rank}rd place`}
                </span>
                {reward.expires_at ? (
                  <span
                    data-testid="redeem-expiry-pill"
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] bg-white/20"
                  >
                    <Clock className="h-3 w-3" />
                    {status === "expired" ? "Expired" : "Expires"}{" "}
                    {new Date(reward.expires_at).toLocaleDateString("en-GB")}
                  </span>
                ) : null}
              </div>
              <p className="mt-3 font-heading text-2xl sm:text-3xl font-bold leading-tight">
                {reward.prize_display}
              </p>
              {reward.sponsor_name ? (
                <p className="text-sm text-white/80 mt-1">Sponsored by {reward.sponsor_name}</p>
              ) : null}
            </div>

            {/* Body */}
            <div className="p-6">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                Winner
              </p>
              <p className="mt-1 font-heading text-lg font-bold text-zinc-950" data-testid="redeem-winner-name">
                {reward.user_name || "—"}
              </p>
              {reward.season_name ? (
                <p className="text-xs text-zinc-500 uppercase tracking-[0.15em] mt-0.5">
                  Season · {reward.season_name}
                </p>
              ) : null}

              <p className="mt-5 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                Voucher code
              </p>
              <p className="mt-1 font-mono tracking-widest text-lg font-bold text-zinc-950" data-testid="redeem-code">
                {reward.code}
              </p>

              {status === "used" && (
                <div
                  className="mt-6 p-4 rounded-sm bg-zinc-100 border border-zinc-200"
                  data-testid="redeem-already-used"
                >
                  <p className="inline-flex items-center gap-2 font-heading font-bold text-zinc-950">
                    <Ban className="h-4 w-4 text-rose-600" /> Already redeemed
                  </p>
                  <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
                    <Clock className="inline h-3.5 w-3.5 mr-1 text-zinc-400" />
                    {new Date(reward.redeemed_at).toLocaleString("en-GB")}
                  </p>
                  {reward.redemption_location ? (
                    <p className="mt-1 text-sm text-zinc-600 leading-relaxed">
                      <MapPin className="inline h-3.5 w-3.5 mr-1 text-zinc-400" />
                      {reward.redemption_location}
                    </p>
                  ) : null}
                  <p className="mt-3 text-xs text-zinc-500">
                    This voucher is single-use. It cannot be redeemed again.
                  </p>
                </div>
              )}

              {status === "success" && (
                <div
                  className="mt-6 p-4 rounded-sm bg-emerald-50 border border-emerald-200"
                  data-testid="redeem-success"
                >
                  <p className="inline-flex items-center gap-2 font-heading font-bold text-emerald-900">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Confirmed!
                  </p>
                  <p className="mt-2 text-sm text-emerald-900/80">
                    The voucher is now marked used at{" "}
                    <strong>{reward.redemption_location || "your venue"}</strong>. Hand over the prize
                    and enjoy the pint.
                  </p>
                </div>
              )}

              {status === "expired" && (
                <div
                  className="mt-6 p-4 rounded-sm bg-rose-50 border border-rose-200"
                  data-testid="redeem-expired"
                >
                  <p className="inline-flex items-center gap-2 font-heading font-bold text-rose-900">
                    <Clock className="h-4 w-4 text-rose-600" /> Voucher expired
                  </p>
                  <p className="mt-2 text-sm text-rose-900/80">
                    This voucher expired on{" "}
                    <strong>{new Date(reward.expires_at).toLocaleDateString("en-GB")}</strong>{" "}
                    and can no longer be redeemed.
                  </p>
                </div>
              )}

              {status === "ready" && (
                <div className="mt-6 pt-6 border-t border-zinc-100">
                  {reward.notes ? (
                    <p className="mb-5 p-3 rounded-sm bg-amber-50 border border-amber-200 text-sm text-amber-900 leading-relaxed">
                      <strong>Redemption note from Dugsy:</strong> {reward.notes}
                    </p>
                  ) : null}

                  <Label className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                    Venue / location (optional)
                  </Label>
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value.slice(0, 120))}
                    placeholder="e.g. The Founders Arms — Bermondsey"
                    data-testid="redeem-location-input"
                    className="mt-1.5 h-11 rounded-sm border-zinc-300"
                  />

                  <Label className="mt-4 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                    Staff note (optional)
                  </Label>
                  <Textarea
                    value={staffNote}
                    onChange={(e) => setStaffNote(e.target.value.slice(0, 240))}
                    placeholder="Anything worth logging (e.g. 'Handed over pint at 21:14')."
                    data-testid="redeem-staff-note"
                    className="mt-1.5 min-h-[70px] rounded-sm border-zinc-300"
                  />

                  {error && (
                    <p className="mt-4 text-sm text-rose-700" data-testid="redeem-form-error">{error}</p>
                  )}

                  <Button
                    onClick={confirm}
                    disabled={submitting}
                    data-testid="redeem-confirm-btn"
                    className="mt-5 w-full h-12 rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white font-bold text-base"
                  >
                    {submitting ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Confirming…</>
                    ) : (
                      <><CheckCircle2 className="h-4 w-4 mr-2" /> Confirm redemption</>
                    )}
                  </Button>
                  <p className="mt-2 text-center text-[11px] text-zinc-400">
                    This action is final — the voucher becomes single-use once confirmed.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-white/40">
          Powered by <Link to="/" className="underline hover:text-white/70">Dugsy&apos;s Football Challenge</Link>
        </p>
      </div>
    </div>
  );
}
